import { createGlobalStyle } from 'antd-style';

import {
  INPUT_BASE_HEIGHT,
  ThemeColors,
  ThemeFont,
  ThemeSpaces,
  hexToRgbaWithAlpha,
} from '../theme';
import { dropdownWithTriangle } from './dropdownMixins';
import {
  absolute,
  absoluteMiddleCenter,
  applyBoxShadowAfter,
  faLike,
  textAfter,
  viewModeStyle,
  withTriangle,
} from './mixins';
import { FA_SELECTOR } from './variables';

const FA_SELECTOR_WITHOUT_ICON_CLASS =
  '.fas:not([class*="icon-"]), .far:not([class*="icon-"]), .fal:not([class*="icon-"])';

function getButtonStyle(theme) {
  return {
    '.ant-btn': {
      lineHeight: 1,
      [`@media (max-width: ${theme.screenSMMax}px)`]: {
        gap: theme.paddingXXS,
      },
      '&.ant-btn-primary, &.ant-btn-color-primary': {
        fontWeight: 900,
        '&:not(:disabled):not(.ant-btn-disabled):focus': {
          backgroundColor: ThemeColors.COLOR_RED7,
        },
        '&.ant-btn-disabled, &.disabled, &[disabled]': {
          '&, &:hover, &:focus': {
            backgroundColor: ThemeColors.COLOR_GREY8_1,
            color: ThemeColors.COLOR_GREY9_9,
          },
        },
        '&.ant-btn-background-ghost:not(:disabled):not(.ant-btn-disabled):focus':
          {
            borderColor: ThemeColors.COLOR_RED7,
            color: ThemeColors.COLOR_RED7,
            backgroundColor: 'transparent',
          },
        '&.ant-btn-background-ghost:disabled': {
          backgroundColor: ThemeColors.COLOR_GREY8_1,
          borderColor: ThemeColors.COLOR_GREY_BLUISH_DISABLED,
          color: ThemeColors.COLOR_RED_DISABLED,
        },
      },
      '&.ant-btn-primary.ant-btn-background-ghost, &.ant-btn-color-primary.ant-btn-background-ghost':
        {
          fontWeight: 500,
        },
      '&.ant-btn-background-ghost:disabled': {
        backgroundColor: 'transparent',
        borderColor: ThemeColors.COLOR_GREY8_1_DISABLED,
      },
      [FA_SELECTOR_WITHOUT_ICON_CLASS]: {
        fontSize: theme.fontSizeLG,
      },
      '.ant-btn-loading-icon': {
        display: 'none',
      },
      '&.ant-btn-loading': {
        '&, &:hover, &:active, &:not(:disabled):not(.ant-btn-disabled):focus, &[disabled]':
          {
            display: 'block',
            cursor: 'not-allowed',
            transitionProperty: 'none',

            '&::before': {
              display: 'none',
            },

            '&.ant-btn-primary, &.ant-btn-color-primary': {
              backgroundColor: ThemeColors.COLOR_RED,
              color: theme.colorWhite,
              borderWidth: 0,
            },
          },
      },
    },
    '.ant-btn-sm': {
      '&.ant-btn-primary, &.ant-btn-color-primary': {
        fontSize: theme.fontSize,
        paddingLeft: ThemeSpaces.SPACE_NORM_20,
        paddingRight: ThemeSpaces.SPACE_NORM_20,
      },
    },
  };
}

function getFormStyle(theme) {
  return {
    '.ant-form-item-label': {
      '.ant-form-inline .ant-form-item > &': {
        display: 'flex',
        alignItems: 'center',

        '> label': {
          height: 'auto',
        },
      },

      '> label': {
        height: INPUT_BASE_HEIGHT,
        fontWeight: 500,
      },

      '> label::before': {
        display: 'none !important',
      },

      '> label::after': {
        ...textAfter(':'),
      },

      '> label.ant-form-item-no-colon::after': {
        content: '""',
      },

      '> label.ant-form-item-no-colon.ant-form-item-required::after': {
        content: '"*"',
      },

      '> label.ant-form-item-required::after': {
        content: '": *"',
      },
    },
    '.ant-form-inline .ant-form-item': {
      [`@media (max-width: ${theme.screenXSMax}px)`]: {
        flexWrap: 'nowrap',

        '.ant-form-item-label': {
          flex: 'none',
          display: 'inline-flex',
          alignItems: 'center',
          marginRight: theme.paddingSM,
          paddingBottom: 0,
        },
        '.ant-form-item-control': {
          display: 'inline-block',
          flex: 'none',
        },
      },
    },
  };
}

function getFormItemMessageStyle(theme) {
  return {
    '.ant-form-item .ant-form-item-explain': {
      minHeight: 0,
      paddingTop: theme.paddingXXS,
      marginBottom: -theme.paddingXXS,
      overflow: 'visible',
      lineHeight: 1,
      fontSize: ThemeFont.FONT_SIZE_SM_PLUS,
      whiteSpace: 'pre-line',
    },
    '.ant-form-item-extra': {
      marginTop: 0,
      textAlign: 'right',
    },
  };
}

function getFormSelectStyle(theme) {
  return {
    '.ant-form-item': {
      '.ant-form-item-control-input': {
        minHeight: 0,
      },
      '.ant-form-item-control-input-content .ant-select': {
        '.ant-select-selector': {
          paddingLeft: ThemeSpaces.SPACE_MD,
          paddingRight: ThemeSpaces.SPACE_MD,

          ' input': {
            fontWeight: 500,
          },
          '.ant-select-selection-item': {
            fontSize: 15,
          },
          ...applyBoxShadowAfter(theme, {
            width: 'auto',
            visibility: 'visible',
          }),
          '.ant-select-selection-search input': {
            ...absolute(),
            height: 'auto',
            padding: 'inherit',
            paddingRight: '52px',
          },
        },
        '&.ant-select-disabled .ant-select-selector': {
          opacity: 0.5,
        },
        '&.qol-input-view-mode .ant-select-selector': {
          ...viewModeStyle(ThemeColors.COLOR_GREY9_9),
        },
        '&.qol-input-view-mode .ant-select-arrow': {
          display: 'none',
        },
        '.ant-select-selection-placeholder': {
          fontSize: theme.fontSize,
        },
        '.ant-select-clear': {
          right: ThemeSpaces.SPACE_XL,
          backgroundColor: 'transparent',
        },
      },
    },
  };
}

function getSelectStyle() {
  return {
    '.ant-select-sm .ant-select-selector': {
      '.ant-select-selection-item, .ant-select-selection-placeholder, input': {
        fontSize: 14,
      },
    },
  };
}

function getFormInputAffixStyle() {
  return {
    '.ant-form-item-control-input-content': {
      '.ant-input-affix-wrapper': {
        height: INPUT_BASE_HEIGHT,
        alignItems: 'center',
        padding: 0,
        boxShadow: 'none',
        fontWeight: 500,

        '&:hover, &:focus, &.ant-input-affix-wrapper-focused': {
          zIndex: 'initial',
        },

        '> .ant-input': {
          height: '100%',
        },

        '> .ant-input-prefix, > .ant-input-suffix': {
          margin: `0 ${ThemeSpaces.SPACE_NORM}px`,
        },

        '&.ant-input-affix-wrapper-disabled': {
          backgroundColor: ThemeColors.COLOR_GREY10,
          opacity: 0.5,
        },
      },
    },
  };
}

function getDatePickerStyle(theme) {
  return {
    '.ant-picker': {
      width: '100%',
      height: INPUT_BASE_HEIGHT,
      padding: 0,
      ...applyBoxShadowAfter(theme),

      '.ant-picker-input': {
        height: '100%',

        input: {
          fontWeight: 500,
          lineHeight: 1,
          padding: 0,
          paddingLeft: theme.paddingSM,
          paddingTop: '1px',

          '&::placeholder': {
            fontWeight: 'normal',
          },
          '&:placeholder-shown': {
            fontSize: theme.fontSize,
            color: ThemeColors.COLOR_GREY_BLUISH,
            lineHeight: 1,
          },
        },
      },

      '.ant-picker-suffix': {
        backgroundColor: 'transparent',

        '&:empty': {
          display: 'none',
        },
      },

      '.ant-picker-clear': {
        backgroundColor: 'transparent',
      },

      '&.ant-picker--special-value': {
        '> .ant-picker-input > input::placeholder': {
          color: theme.colorText,
          fontWeight: 500,
        },
      },
      '&.ant-picker-focused, &:hover': {
        '.ant-picker-suffix:not(:last-child)': {
          opacity: 1,
        },
      },

      '&.ant-picker-disabled': {
        opacity: 0.5,

        '.ant-picker-input': {
          [FA_SELECTOR]: {
            color: theme.colorText,
          },
          input: {
            '&, &::placeholder': {
              color: theme.colorText,
            },
          },
        },
      },
    },
  };
}

function getDatePickerDropdownStyle() {
  return {
    '.ant-picker-dropdown': {
      '.ant-picker-date-panel, .ant-picker-month-panel, .ant-picker-year-panel':
        {
          width: '272px',
        },

      '.ant-picker-date-panel .ant-picker-body': {
        padding: '4px 8px',

        '.ant-picker-content': {
          width: '252px',
        },
      },

      '.ant-picker-header > button': {
        color: '#00000040',
      },

      '.ant-picker-header-view': {
        fontWeight: 500,
      },

      '.ant-picker-ranges': {
        lineHeight: '26px',
      },

      '.ant-picker-footer-extra': {
        padding: 0,
      },
    },
  };
}

function getInputViewModeStyle() {
  return {
    [`.ant-form-item-control-input-content .ant-input-affix-wrapper,
      .ant-picker.ant-picker-disabled,
    `]: {
      '&.qol-input-view-mode, &.qol-input-view-mode:hover': {
        ...viewModeStyle(ThemeColors.COLOR_GREY9_9),
      },
    },
  };
}

function getCheckboxStyle(theme) {
  return {
    '.ant-checkbox-wrapper': {
      display: 'flex',
      alignItems: 'center',

      '.ant-checkbox': {
        top: 0,
      },

      '&.vertical, &.vertical-reverse': {
        gap: theme.paddingXXS,
        '.ant-checkbox': {
          marginTop: 0,
          '& + span': { paddingLeft: 0 },
        },
      },

      '&.vertical': {
        flexDirection: 'column',
      },

      '&.vertical-reverse': {
        flexDirection: 'column-reverse',
      },

      '> *': { display: 'block' },
      '&::after': { display: 'none' },
    },

    '.ant-checkbox + span': {
      paddingLeft: theme.paddingXS,
      paddingRight: 0,
    },

    '.ant-checkbox-inner': {
      '.ant-form-item-has-error .ant-checkbox-wrapper &': {
        '&, &:hover': {
          borderColor: theme.colorError,
        },
      },

      '.ant-checkbox-checked &': {
        '&::after': { display: 'none' },

        '&::before': {
          ...faLike('\\f00c'),
          ...absoluteMiddleCenter(),
          color: theme.colorWhite,
          fontSize: '0.68em',
          margin: '1px 0 0 0',
        },
      },

      '.ant-checkbox-disabled &': {
        borderColor: '#d9d9d9',
      },
    },
  };
}

function getRadioStyle(theme) {
  return {
    '.ant-radio-group': {
      fontSize: 0,
    },

    '.ant-radio-wrapper': {
      display: 'inline-flex',
      flexDirection: 'row-reverse',
      alignItems: 'center',
      justifyContent: 'center',

      '&.ant-radio-wrapper-disabled': {
        cursor: 'not-allowed',
      },

      '> *': {
        display: 'block',
        position: 'initial',
      },

      '&::after': {
        display: 'none',
      },

      '.ant-radio-inner': {
        '.ant-radio-input:focus + &': {
          boxShadow: 'none',
        },
      },
    },
    '.ant-radio-wrapper .ant-radio': {
      '+ span': {
        fontWeight: 500,
        paddingLeft: 0,
        paddingRight: theme.paddingXS,
      },

      '&.ant-radio-checked::after': {
        display: 'none',
      },
    },
    '.ant-form-item-has-error .ant-radio-wrapper-in-form-item .ant-radio-inner':
      {
        borderColor: theme.colorError,
      },
  };
}

function getSwitchStyle() {
  return {
    '.ant-switch': {
      '&, &:focus, &:focus:hover, &:active:hover': {
        boxShadow: `0 0 0 1px ${ThemeColors.COLOR_GREY_DARK5}, 1px 1px 6px 0 inset ${ThemeColors.COLOR_BLACK4}`,
      },
      '&.ant-switch-checked': {
        '&, &:focus, &:focus:hover, &:active:hover': {
          boxShadow: `0 0 0 1px ${ThemeColors.COLOR_RED_DARK2}, 1px 1px 6px 0 inset ${ThemeColors.COLOR_RED_DARK2}`,
        },
      },
    },
  };
}

function getModalStyle() {
  return {
    '.ant-modal-root': {
      '.ant-modal-mask': {
        backdropFilter: 'blur(3px)',
        zIndex: 1031,
      },
      '.ant-modal-wrap': {
        zIndex: 1032,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        paddingTop: '120px',
        overflow: 'hidden',

        '.ant-modal': {
          top: 0,
          paddingBottom: 0,

          '.ant-modal-content': {
            padding: 0,
          },
        },

        '&.ant-modal-centered': {
          alignItems: 'center',
          paddingTop: 0,

          '&::before': {
            display: 'none',
          },
          '.ant-modal': {
            display: 'block',
            textAlign: 'initial',
            flex: 'initial',
            margin: 0,
          },
        },
      },
    },
  };
}

function getMessageStyle(theme) {
  return {
    '.ant-message-custom-content': {
      borderRadius: 'inherit',
      fontWeight: 'bold',
      padding: `0 ${theme.paddingSM}px`,
      whiteSpace: 'nowrap',
      gap: theme.paddingSM,
      minHeight: 30,

      '> *': {
        display: 'block',
      },
      [FA_SELECTOR]: {
        fontSize: theme.fontSizeXL,
      },
      '&.ant-message-success': {
        backgroundColor: ThemeColors.COLOR_LIGHT_GREEN,
      },
      '&.ant-message-error': {
        backgroundColor: ThemeColors.COLOR_RED,
      },
      '&.ant-message-info': {
        backgroundColor: ThemeColors.COLOR_ORANGE2,
      },
      '&.ant-message-warning': {
        backgroundColor: ThemeColors.COLOR_WHITE2,
      },
    },
  };
}

function getTableStyle() {
  return {
    // `tableScrollBg` component token is overwritten by antd's own Table
    // style generator (derived from `colorSplit`), so it can't be changed
    // via theme.js component tokens — override the CSS property directly.
    '.ant-table-wrapper .ant-table': {
      scrollbarColor: 'rgba(0, 0, 0, 0.25) transparent !important',
    },
  };
}

function getTypographyStyle() {
  return {
    'h4.ant-typography, .ant-typography h4': {
      fontWeight: 500,
    },
  };
}

function getTagStyle() {
  return {
    '.ant-tag': {
      fontWeight: 'bold',
      display: 'inline-flex',
      alignItems: 'center',

      '.ant-tag-close-icon': {
        display: 'flex',
      },
    },
  };
}

function getTooltipStyle(theme) {
  const dropShadowColor = hexToRgbaWithAlpha(
    ThemeColors.COLOR_GREY_BLUISH3,
    0.65
  );

  return {
    '.ant-tooltip': {
      '.ant-tooltip-inner': {
        fontWeight: 'bold',
        padding: `${theme.paddingSM}px ${ThemeSpaces.SPACE_NORM}px`,
      },
      '.ant-tooltip-arrow': {
        display: 'none',
      },
      '&.ant-tooltip-placement-top .ant-tooltip-inner': {
        ...withTriangle({
          width: 12,
          height: 7,
          direction: 'down',
          position: { left: '50%', top: '100%', transform: 'translateX(-50%)' },
          backgroundColor: ThemeColors.COLOR_ORANGE2,
          borderColor: theme.colorWhite,
          dropShadow: `drop-shadow(0 2px 6px ${dropShadowColor})`,
        }),
      },
      '&.ant-tooltip-placement-bottom .ant-tooltip-inner': {
        ...withTriangle({
          width: 12,
          height: 7,
          direction: 'up',
          position: {
            left: '50%',
            bottom: '100%',
            transform: 'translateX(-50%)',
          },
          backgroundColor: ThemeColors.COLOR_ORANGE2,
          borderColor: theme.colorWhite,
          dropShadow: `drop-shadow(0 2px 6px ${dropShadowColor})`,
        }),
      },
      '&.ant-tooltip-placement-topRight .ant-tooltip-inner': {
        ...withTriangle({
          width: 12,
          height: 7,
          direction: 'down',
          position: { right: '10%', top: '100%' },
          backgroundColor: ThemeColors.COLOR_ORANGE2,
          borderColor: theme.colorWhite,
          dropShadow: `drop-shadow(0 2px 6px ${dropShadowColor})`,
        }),
      },
      '&.ant-tooltip-placement-topLeft .ant-tooltip-inner': {
        ...withTriangle({
          width: 12,
          height: 7,
          direction: 'down',
          position: { left: '10%', top: '100%' },
          backgroundColor: ThemeColors.COLOR_ORANGE2,
          borderColor: theme.colorWhite,
          dropShadow: `drop-shadow(0 2px 6px ${dropShadowColor})`,
        }),
      },
    },
  };
}

function getMenuStyle() {
  return {
    '.ant-menu .ant-menu-title-content': {
      transition: 'none',
    },
  };
}

function getDropdownSubmenuStyle() {
  const submenuPopupBase = dropdownWithTriangle({
    menuSelector: '.ant-dropdown-menu-sub',
    menuCustom: {
      width: 120,
      backgroundColor: ThemeColors.COLOR_WHITE4,
      marginLeft: '-5px !important',
      marginTop: -2,
    },
    menuItemCustom: { color: ThemeColors.COLOR_GREY6 },
  });
  return {
    '.ant-dropdown-menu-submenu-popup': {
      ...submenuPopupBase,

      '.ant-dropdown-menu-sub': {
        ...submenuPopupBase['.ant-dropdown-menu-sub'],

        '.ant-dropdown-menu-item': {
          ...submenuPopupBase['.ant-dropdown-menu-sub'][
            '.ant-dropdown-menu-item'
          ],

          '&:nth-child(even)': {
            backgroundColor: '#e5ebf0',
          },
          '&:nth-child(odd)': {
            backgroundColor: ThemeColors.COLOR_WHITE4,
          },
          '&:hover': {
            backgroundColor: `${ThemeColors.COLOR_RED} !important`,
          },
        },
      },
    },

    '.ant-dropdown-menu-submenu-vertical.ant-dropdown-menu-submenu-open': {
      '> .ant-dropdown-menu-submenu-title': {
        backgroundColor: `${ThemeColors.COLOR_RED} !important`,
        color: `${ThemeColors.COLOR_WHITE} !important`,
        borderRadius: '0 !important',

        '.ant-dropdown-menu-submenu-expand-icon': {
          color: `${ThemeColors.COLOR_WHITE} !important`,
          fontSize: 18,
        },
      },
    },
  };
}

function getStepsStyle() {
  const circlePadding = 5;
  const outerCircleSize = 30 + circlePadding * 2;
  const boxShadowColor = hexToRgbaWithAlpha(ThemeColors.COLOR_GREY8_7, 0.65);
  const boxShadow = `0 4px 6px ${boxShadowColor}`;

  return {
    '.ant-steps.ant-steps-horizontal': {
      paddingTop: 5,

      '.ant-steps-item-icon': {
        position: 'relative',
        '&::before': {
          content: '" "',
          ...absoluteMiddleCenter(),
          width: outerCircleSize,
          height: outerCircleSize,
          backgroundColor: ThemeColors.COLOR_GREY9_9,
          borderRadius: '100%',
          border: `1px solid ${ThemeColors.COLOR_GREY7_7}`,
          boxShadow,
        },
      },
      '.ant-steps-item-title': {
        fontWeight: 'bold',
        whiteSpace: 'pre-line',
      },
      '.ant-steps-item-finish > .ant-steps-item-container': {
        '.ant-steps-item-title': {
          color: ThemeColors.COLOR_GREY4,
        },

        '.ant-steps-item-tail::after': {
          left: 12,
          right: 13,
          borderTop: `1px solid ${ThemeColors.COLOR_RED5}`,
          borderBottom: `1px solid ${ThemeColors.COLOR_RED5}`,
        },
      },
      '&, .ant-steps-item-wait, .ant-steps-item-process': {
        '.ant-steps-item-tail': {
          top: '7px',
          height: 18,
          border: `1px solid ${ThemeColors.COLOR_GREY7_7}`,

          '&::before': {
            content: '" "',
            ...absolute(16),
            backgroundColor: ThemeColors.COLOR_GREY9_9,
            zIndex: 1,
            boxShadow,
          },
          '&::after': {
            ...absolute(16, 3),
            zIndex: 2,
            borderRadius: 0,
            height: 'auto',
            width: 'auto',
          },
        },
      },
    },
  };
}

function getTransferStyle(theme) {
  return {
    '.ant-transfer': {
      height: '100%',

      '.ant-form-item-row': {
        height: '100%',
      },

      '.ant-transfer-list-body-customize-wrapper': {
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        overflow: 'hidden',
        height: '100%',
      },

      '.ant-transfer-list-body': {
        flexGrow: 1,
      },

      '.ant-transfer-operation': {
        alignSelf: 'flex-start',

        '.ant-btn.ant-btn-primary': {
          boxShadow: `0 2px 10px ${ThemeColors.COLOR_BLACK1_5}`,
          width: 40,
          height: 28,
          display: 'flex',
          marginBottom: 10,

          '&, &:hover, &:active, &:focus': {
            backgroundColor: 'transparent',
            color: ThemeColors.COLOR_GREY_BLUISH7_4,
            border: `1px solid ${ThemeColors.COLOR_GREY_BLUISH7_4}`,
          },

          '&[disabled]': {
            opacity: 0.5,
          },

          '.anticon': {
            display: 'none',
          },

          '.fa-chevron-left, .fa-chevron-right': {
            margin: 0,
          },
        },
      },
    },

    '.ant-transfer-list-header': {
      background: ThemeColors.COLOR_GREY_DARK3,
      borderColor: ThemeColors.COLOR_BLACK2,
      color: ThemeColors.COLOR_GREY_BLUISH8,
      flexDirection: 'row-reverse',
      padding: '0 10px',
    },

    '.ant-transfer-list': {
      borderColor: `${ThemeColors.COLOR_GREY_DARK3} !important`,
      minWidth: 0,
      width: '330px !important',
      flexBasis: '0 !important',

      '.ant-checkbox-inner': {
        width: 18,
        height: 18,
      },

      '.ant-checkbox-wrapper .ant-checkbox.ant-checkbox-checked': {
        '.ant-checkbox-inner': {
          backgroundColor: ThemeColors.COLOR_GREY_BLUISH3,
          borderColor: ThemeColors.COLOR_GREY_BLUISH3,
        },
      },
    },

    '.ant-transfer-list-header-title': {
      textAlign: 'left',
      textTransform: 'uppercase',
      color: ThemeColors.COLOR_GREY_BLUISH8,
      fontFamily: ThemeFont.FONT_CONDENSED,
      fontWeight: 700,
    },

    '.ant-transfer-list-body-search-wrapper': {
      height: 60,
      display: 'flex',
      alignItems: 'center',
      backgroundColor: ThemeColors.COLOR_GREY_DARK2_7,
      padding: '0 10px',
      borderTop: `1px solid ${ThemeColors.COLOR_GREY3_5}`,

      '.ant-transfer-list-search': {
        '.ant-input': {
          borderRadius: 4,
          fontFamily: theme.fontFamily,
          fontWeight: 500,
          fontSize: 16,
          color: `${ThemeColors.COLOR_GREY7_1} !important`,

          '&, &:hover, &:active, &:focus': {
            borderColor: ThemeColors.COLOR_GREY_DARK1_3,
            boxShadow: 'none',
          },
        },

        '&, ::placeholder': {
          color: ThemeColors.COLOR_GREY11,
          fontWeight: 500,
        },

        '.ant-input-prefix': {
          '.anticon, .anticon-search': {
            color: ThemeColors.COLOR_GREY11,
            fontSize: 18,
          },
        },

        '.ant-input-suffix': {
          '.anticon': {
            color: ThemeColors.COLOR_GREY11,
            fontSize: 12,
          },
        },
      },
    },

    '.ant-transfer-list-header-selected': {
      color: ThemeColors.COLOR_GREY_BLUISH7_4,
      fontFamily: ThemeFont.FONT_CONDENSED,
      fontWeight: 700,
      textTransform: 'uppercase',
    },
  };
}

const AntdOverrides = createGlobalStyle(({ theme }) => ({
  ...getButtonStyle(theme),
  ...getFormStyle(theme),
  ...getFormItemMessageStyle(theme),
  ...getFormSelectStyle(theme),
  ...getSelectStyle(),
  ...getFormInputAffixStyle(),
  ...getDatePickerStyle(theme),
  ...getDatePickerDropdownStyle(),
  ...getCheckboxStyle(theme),
  ...getRadioStyle(theme),
  ...getSwitchStyle(),
  ...getInputViewModeStyle(),
  ...getModalStyle(),
  ...getMessageStyle(theme),
  ...getTableStyle(),
  ...getTypographyStyle(),
  ...getTagStyle(),
  ...getTooltipStyle(theme),
  ...getMenuStyle(),
  ...getDropdownSubmenuStyle(),
  ...getStepsStyle(),
  ...getTransferStyle(theme),
}));

export default AntdOverrides;
